import { Navigate, Outlet, useLocation } from 'react-router';

import { useAuth } from '@/lib/auth/AuthContext';
import { BootSplash } from '@/components/layout/BootSplash';
import { routes } from './paths';

/**
 * Route guard. Two things it must get right:
 *
 *  - While tokens are being validated we render a splash, not the login screen. Flashing
 *    "Entrar" at a user who *is* logged in is the most common bug in this pattern.
 *  - The attempted location is carried into the redirect so that signing in returns the
 *    user to where they were going. A 1-day access token means this happens routinely.
 */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <BootSplash />;

  if (status === 'anonymous') {
    return <Navigate to={routes.login} replace state={{ from: location }} />;
  }

  return <Outlet />;
}

/**
 * The inverse: keep an authenticated user out of the login and sign-up screens.
 *
 * This guard, not the form, owns the post-login redirect. The moment `login()` flips the
 * status, this component re-renders and navigates — so a `navigate()` inside the form's
 * submit handler would race with it and lose. Reading the same `from` state that
 * RequireAuth wrote is what actually returns the user to where they were going.
 */
export function RequireAnonymous() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <BootSplash />;

  if (status === 'authenticated') {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={from ?? routes.home} replace />;
  }

  return <Outlet />;
}
